// src/monitoring/pages/DataSources.jsx - the parks and where their data comes from.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMonitoring } from "../MonitoringContext";
import { useMonitoringText } from "../i18n";
import { createSource, deleteSource, fetchSources, fetchVendors, pollSource, seedDemo, seedProjectSimulation, testSource, updateSource } from "../services/monitoringApi";
import SourceDialog from "../components/SourceDialog";
import CsvReplayDialog from "../components/CsvReplayDialog";
import { SectionTitle, SimulatedBadge, fmtDateTime, fmtKw, fmtKwp, useLocale } from "../components/primitives";

export default function DataSources() {
  const m = useMonitoring();
  const t = useMonitoringText();
  const locale = useLocale();
  const [sources, setSources] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [dialog, setDialog] = useState({ open: false, source: null });
  const [busy, setBusy] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [projectSimOpen, setProjectSimOpen] = useState(false);
  const [projectSimDays, setProjectSimDays] = useState(310);
  const [replaceDemo, setReplaceDemo] = useState(true);
  const [projectSimulating, setProjectSimulating] = useState(false);
  const [csvDialog, setCsvDialog] = useState({ open: false, source: null });

  const load = useCallback(() => {
    if (!m.projectId || !m.accessToken) return;
    fetchSources(m.projectId, { accessToken: m.accessToken }).then((r) => setSources(r.sources)).catch((e) => setError(e.message));
  }, [m.projectId, m.accessToken]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!m.accessToken) return;
    fetchVendors({ accessToken: m.accessToken }).then((r) => setVendors(r.vendors)).catch((e) => setError(e.message));
  }, [m.accessToken]);

  const submit = async (body) => {
    if (dialog.source) await updateSource(dialog.source.id, body, { accessToken: m.accessToken });
    else await createSource(m.projectId, body, { accessToken: m.accessToken });
    load(); m.refresh();
  };
  const run = async (id, fn, done) => {
    setBusy(id); setNote(null); setError(null);
    try { const r = await fn(); done?.(r); load(); m.refresh(); } catch (e) { setError(e.message); } finally { setBusy(null); }
  };
  const remove = (s) => {
    if (!window.confirm(t("sources.deleteConfirm"))) return;
    run(s.id, () => deleteSource(s.id, { accessToken: m.accessToken }));
  };
  const loadDemo = async () => {
    setSeeding(true); setNote(null); setError(null);
    try { await seedDemo(m.projectId, 14, { accessToken: m.accessToken }); setNote(t("sources.demoDone")); load(); m.refresh(); }
    catch (e) { setError(e.message); } finally { setSeeding(false); }
  };
  const simulateProject = async () => {
    setProjectSimulating(true); setNote(null); setError(null);
    try {
      const r = await seedProjectSimulation(m.projectId, { operating_days: Number(projectSimDays), replace_demo: replaceDemo }, { accessToken: m.accessToken });
      if (r.poll?.status !== "ok") throw new Error(r.poll?.error || t("sources.projectSimFailed"));
      setProjectSimOpen(false);
      setNote(t("sources.projectSimDone", { name: r.source_name, days: r.operating_days }));
      load(); m.refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setProjectSimulating(false);
    }
  };
  const pollStatus = (s) => {
    if (!s.enabled) return <Chip size="small" label={t("sources.disabled")} />;
    if (!s.last_poll_at) return <Chip size="small" variant="outlined" label={t("sources.poll.never")} />;
    const color = s.last_poll_status === "ok" ? "success" : s.last_poll_status === "not_wired" ? "info" : "error";
    const chip = <Chip size="small" color={color} variant={color === "success" ? "outlined" : "filled"} label={t(`sources.poll.${s.last_poll_status}`) || s.last_poll_status} />;
    return s.last_poll_error ? <Tooltip title={s.last_poll_error} arrow>{chip}</Tooltip> : chip;
  };

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <SectionTitle title={t("sources.title")} hint={t("sources.subtitle")}
          action={<Stack direction="row" spacing={1}>
            {m.canSeedDemo && <Button variant="outlined" onClick={() => setProjectSimOpen(true)} disabled={projectSimulating}>{t("sources.projectSim")}</Button>}
            {m.canSeedDemo && <Button variant="outlined" onClick={loadDemo} disabled={seeding}>{seeding ? t("sources.demoRunning") : t("sources.demo")}</Button>}
            <Button variant="outlined" onClick={() => setCsvDialog({ open: true, source: null })}>{t("csv.open")}</Button>
            <Button variant="contained" onClick={() => setDialog({ open: true, source: null })} disabled={!vendors.length}>{t("sources.add")}</Button>
          </Stack>} />
        {m.canSeedDemo && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>{t("sources.demoHint")}</Typography>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {note && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setNote(null)}>{note}</Alert>}
        {sources === null ? <CircularProgress size={22} /> : sources.length === 0 ? <Typography color="text.secondary">{t("sources.empty")}</Typography> : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>{["park", "vendor", "capacity", "interval", "lastPoll", "lastReading", "status"].map((k) => <TableCell key={k}>{t(`sources.col.${k}`)}</TableCell>)}<TableCell /></TableRow>
              </TableHead>
              <TableBody>
                {sources.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.plant?.inverter_model || ""}{s.plant?.inverter_count ? ` × ${s.plant.inverter_count}` : ""}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{s.vendor_label}</span>
                        {s.simulated && <SimulatedBadge />}
                        {s.replay && <Chip size="small" color="info" variant="outlined" label={t("csv.badge")} />}
                        {!s.wired && !s.simulated && !s.replay && <Tooltip title={t("sources.wiredHint")} arrow><Chip size="small" color="info" variant="outlined" label={t("status.not_wired")} /></Tooltip>}
                      </Stack>
                      {s.vendor_site_id && <Typography variant="caption" color="text.secondary">{s.vendor_site_id}</Typography>}
                    </TableCell>
                    <TableCell>{fmtKwp(s.plant?.dc_kwp, locale)}{s.plant?.ac_kw ? ` / ${fmtKw(s.plant.ac_kw, locale)}` : ""}</TableCell>
                    <TableCell>{s.replay ? t("csv.sampleEvery", { n: s.poll_interval_min }) : t("sources.everyMin", { n: s.poll_interval_min })}</TableCell>
                    <TableCell>{fmtDateTime(s.last_poll_at, locale, s.plant?.timezone)}</TableCell>
                    <TableCell>{fmtDateTime(s.last_reading_at, locale, s.plant?.timezone)}</TableCell>
                    <TableCell>{pollStatus(s)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {s.replay ? (
                        <Button size="small" onClick={() => setCsvDialog({ open: true, source: s })}>{t("csv.append")}</Button>
                      ) : <>
                        <Button size="small" disabled={busy === s.id} onClick={() => run(s.id, () => pollSource(s.id, { accessToken: m.accessToken }),
                          (r) => setNote(r.status === "ok" ? t("sources.polled", { n: r.readings, alarms: r.alarms?.active ?? 0 }) : r.error))}>{t("sources.pollNow")}</Button>
                        <Button size="small" disabled={busy === s.id} onClick={() => run(s.id, () => testSource(s.id, { accessToken: m.accessToken }),
                          (r) => (r.ok ? setNote(`${t("sources.testOk")} ${r.detail || ""}`) : setError(`${t("sources.testFailed")}: ${r.detail}`)))}>{t("sources.test")}</Button>
                      </>}
                      <IconButton size="small" aria-label={t("sources.edit")} onClick={() => setDialog({ open: true, source: s })}><EditOutlinedIcon fontSize="small" /></IconButton>
                      <IconButton size="small" aria-label={t("sources.delete")} disabled={busy === s.id} onClick={() => remove(s)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
      <SourceDialog open={dialog.open} source={dialog.source} vendors={vendors} onClose={() => setDialog({ open: false, source: null })} onSubmit={submit} />
      <CsvReplayDialog
        open={csvDialog.open}
        source={csvDialog.source}
        projectId={m.projectId}
        accessToken={m.accessToken}
        onClose={() => setCsvDialog({ open: false, source: null })}
        onImported={(result) => {
          setNote(t("csv.imported", { rows: result.summary?.readings, devices: result.summary?.devices }));
          load(); m.refresh();
        }}
      />
      <Dialog open={projectSimOpen} onClose={() => !projectSimulating && setProjectSimOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("sources.projectSimTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">{t("sources.projectSimHint")}</Alert>
            <TextField
              type="number"
              label={t("sources.projectSimDays")}
              value={projectSimDays}
              onChange={(e) => setProjectSimDays(Math.min(365, Math.max(7, Number(e.target.value) || 7)))}
              helperText={t("sources.projectSimDaysHint")}
              slotProps={{ htmlInput: { min: 7, max: 365 } }}
            />
            <FormControlLabel
              control={<Checkbox checked={replaceDemo} onChange={(e) => setReplaceDemo(e.target.checked)} />}
              label={t("sources.projectSimReplace")}
            />
            <Typography variant="caption" color="text.secondary">{t("sources.projectSimForecast")}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectSimOpen(false)} disabled={projectSimulating}>{t("form.cancel")}</Button>
          <Button variant="contained" onClick={simulateProject} disabled={projectSimulating || projectSimDays < 7 || projectSimDays > 365}
            startIcon={projectSimulating ? <CircularProgress size={16} color="inherit" /> : null}>
            {projectSimulating ? t("sources.projectSimRunning") : t("sources.projectSimCreate")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
